import * as Headless from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';

interface FullscreenDialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Extra classes for the panel (e.g. to override the default width/height). */
  className?: string;
  /** Extra classes for the scrollable body wrapper. */
  bodyClassName?: string;
}

// Reusable near-fullscreen "peek" dialog: a centered ~95vw × 90vh panel with a
// backdrop, a close (X) button, and a scrollable body. Built on a raw Headless
// `Dialog` at z-[60] (above app drawers/overlays at z-50, and Tailwind's z scale
// stops at 50 so the arbitrary value is required) — Catalyst's `Dialog` caps at
// max-w-12xl and has no settable z-index, so it can't be used for this.
export function FullscreenDialog({
  open,
  onClose,
  children,
  className,
  bodyClassName,
}: FullscreenDialogProps) {
  return (
    <Headless.Dialog open={open} onClose={onClose} className="relative z-[60]">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-zinc-950/40 transition duration-150 data-closed:opacity-0 dark:bg-zinc-950/60"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Headless.DialogPanel
          transition
          className={twMerge(
            'relative flex h-[90vh] w-[95vw] max-w-[95vw] transform flex-col overflow-hidden rounded-xl bg-white shadow-2xl transition duration-150 data-closed:scale-95 data-closed:opacity-0 dark:border dark:border-zinc-700 dark:bg-zinc-900',
            className,
          )}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 z-10 rounded p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            <XMarkIcon className="size-6" />
          </button>
          <div className={twMerge('flex-1 overflow-y-auto px-6 py-5', bodyClassName)}>{children}</div>
        </Headless.DialogPanel>
      </div>
    </Headless.Dialog>
  );
}
